import {myEnrollmentsApi} from '@/api/my-enrollments.api.js'
import {useFlashMessages} from '@/composables/use-flash-messages.js'
import {enrollmentStatuses} from '@/constants/enrollments.js'
import {ref} from 'vue'

export function useResumeEnrollment(enrollment) {
  const {setFlashSuccess, setFlashFailure} = useFlashMessages()

  //The server's start_or_stop_enrollment is a toggle, not a dedicated resume endpoint.
  //Calling it twice in quick succession (paused -> active -> paused) would leave the
  //learner exactly where they started. If two RESUME messages arrive before the first
  //API call finishes, we must make sure only one call goes out.
  //
  //We do this by storing the in-flight promise here before we await it. The next caller
  //checks this ref, finds the promise already running, and waits for it instead of
  //firing a second request. We can't just await the function directly because then we'd
  //never have a reference to share with the second caller.
  //
  //Once the promise settles we clear this ref so a future RESUME can start fresh.
  const resumeEffort = ref(null)

  const executeResume = async () => {
    try {
      const json = await myEnrollmentsApi.startOrStopEnrollment(enrollment.value.id)
      enrollment.value.status = json.enrollment.status
      enrollment.value.pausedAt = json.enrollment.pausedAt
    }
    finally {
      resumeEffort.value = null
    }
  }

  const resumeEnrollment = async () => {
    if (!enrollment.value?.isPaused) {
      return false
    }
    const inFlight = resumeEffort.value
    if (inFlight) {
      await inFlight.catch(() => {})
      return false
    }
    const effort = executeResume()
    resumeEffort.value = effort
    try {
      await effort
      if (enrollment.value?.status === enrollmentStatuses.PAUSED) {
        setFlashFailure(`Couldn't resume the course. Please try again.`)
        return false
      }
      setFlashSuccess('Your enrollment has been resumed!')
      return true
    }
    catch {
      setFlashFailure(`Couldn't resume the course. Please try again.`)
      return false
    }
  }

  return {resumeEnrollment}
}
